import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Polyline, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet-polylinedecorator';
import axios from 'axios';
import * as toGeoJSON from '@tmcw/togeojson';
import 'leaflet/dist/leaflet.css';
import AutoTopoTileLayer from './AutoTopoTileLayer';
import ExternalPoisLayer, { EXTERNAL_POI_CATEGORIES } from './ExternalPoisLayer';
import MunicipioLiveLayers from './MunicipioLiveLayers';
import CompanyLiveLayers from './CompanyLiveLayers';
import { WebMapLegendButton, WebMapLegendPanel, useLegendToggle } from './WebMapLegend';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const ROUTE_COLOR = '#E91E63'; // magenta

// Balão coloridos para início (verde) e fim (vermelho) da rota
const makeBalloonIcon = (color, label) =>
  L.divIcon({
    className: 'tectur-balloon-icon',
    html: `<div style="position:relative;width:32px;height:42px;">
      <svg viewBox="0 0 32 42" width="32" height="42">
        <path d="M16 0 C7.2 0 0 7.2 0 16 c0 12 16 26 16 26 s16 -14 16 -26 C32 7.2 24.8 0 16 0 z"
          fill="${color}" stroke="#fff" stroke-width="2"/>
        <circle cx="16" cy="16" r="6" fill="#fff"/>
      </svg>
      <div style="position:absolute;top:8px;left:0;right:0;text-align:center;color:${color};font-size:12px;font-weight:900;">${label}</div>
    </div>`,
    iconSize: [32, 42],
    iconAnchor: [16, 42],
    popupAnchor: [0, -38],
  });

const startIcon = makeBalloonIcon('#16A34A', 'A');
const endIcon = makeBalloonIcon('#DC2626', 'B');

// Componente que desenha setas direccionais ao longo da polyline
function DirectionArrows({ coordinates }) {
  const map = useMap();
  useEffect(() => {
    if (!coordinates || coordinates.length < 2) return undefined;
    const line = L.polyline(coordinates);
    const decorator = L.polylineDecorator(line, {
      patterns: [
        {
          offset: 40,
          repeat: 80,
          symbol: L.Symbol.arrowHead({
            pixelSize: 12,
            polygon: false,
            pathOptions: { stroke: true, color: ROUTE_COLOR, weight: 3, opacity: 1 },
          }),
        },
      ],
    });
    decorator.addTo(map);
    return () => {
      try { decorator.remove(); } catch { /* noop */ }
    };
  }, [coordinates, map]);
  return null;
}

// POI icon (copper-colored)
const poiIcon = L.divIcon({
  className: 'tectur-poi-icon',
  html: '<div style="width:28px;height:28px;background:#8B4513;border:3px solid #fff;border-radius:50%;box-shadow:0 2px 6px rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;color:white;font-size:14px;font-weight:bold;">★</div>',
  iconSize: [28, 28],
  iconAnchor: [14, 14],
  popupAnchor: [0, -14],
});

// Component to fit bounds — SÓ na primeira vez que a rota abre. Evita que
// o mapa "salte para o sítio" quando o utilizador edita/apaga POIs (que
// origina novo `bounds` mas o utilizador não quer perder o pan/zoom actual).
function FitBounds({ bounds, routeId }) {
  const map = useMap();
  const fittedForRef = React.useRef(null);

  useEffect(() => {
    if (!bounds || bounds.length === 0) return;
    if (fittedForRef.current === routeId) return; // já ajustámos para esta rota
    map.fitBounds(bounds, { padding: [50, 50] });
    fittedForRef.current = routeId;
  }, [bounds, map, routeId]);

  return null;
}

// Component to fly to a specific POI (map.flyTo) quando o user clica no POI
// da lista. Mantém o zoom actual se já estiver > 14, caso contrário faz zoom
// aproximado (zoom 16).
function FlyToPOI({ poi }) {
  const map = useMap();
  useEffect(() => {
    if (!poi || poi.latitude == null || poi.longitude == null) return;
    const currentZoom = map.getZoom() || 14;
    const targetZoom = Math.max(currentZoom, 16);
    map.flyTo([poi.latitude, poi.longitude], targetZoom, { duration: 0.8 });
  }, [poi, map]);
  return null;
}

function ResizeOnFullscreen() {
  const map = useMap();
  useEffect(() => {
    const onChange = () => setTimeout(() => map.invalidateSize(), 200);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, [map]);
  return null;
}

function FullscreenButton({ targetRef }) {
  const [isFs, setIsFs] = useState(false);
  const [showHint, setShowHint] = useState(false);
  useEffect(() => {
    const onChange = () => {
      const fs = document.fullscreenElement === targetRef.current;
      setIsFs(fs);
      setShowHint(fs);
    };
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, [targetRef]);
  useEffect(() => {
    if (!showHint) return undefined;
    const t = setTimeout(() => setShowHint(false), 6000);
    return () => clearTimeout(t);
  }, [showHint]);
  if (!document.fullscreenEnabled) return null;
  const toggle = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else targetRef.current?.requestFullscreen();
  };
  return (
    <>
      <button
        onClick={toggle}
        className="absolute bottom-6 right-3 z-[500] h-10 w-10 rounded-lg bg-white/95 text-slate-800 border border-slate-300 shadow-md text-lg font-bold hover:border-slate-500"
        title={isFs ? 'Sair do ecrã inteiro (Esc)' : 'Mapa em ecrã inteiro'}
        data-testid="map-fullscreen-btn"
      >
        {isFs ? '✕' : '⛶'}
      </button>
      {showHint && (
        <div
          className="absolute top-4 left-1/2 -translate-x-1/2 z-[600] rounded-lg bg-slate-900/90 text-white px-4 py-3 text-sm shadow-lg flex items-center gap-3"
          data-testid="map-fullscreen-hint"
        >
          <span>Mapa em ecrã inteiro. Para sair, carregue na tecla <b>Esc</b>.</span>
          <button onClick={() => setShowHint(false)} className="text-white/70 hover:text-white" data-testid="map-fullscreen-hint-close">✕</button>
        </div>
      )}
    </>
  );
}

const RouteMap = ({ route, focusPOI, liveOverlay = false, liveOverlayMode = 'municipio', companyId = null }) => {
  const wrapRef = useRef(null);
  // segments: array de arrays — cada inner array é UM trkseg/LineString
  // independente. Permite renderizar polylines SEPARADAS (sem juntar o
  // fim de um segmento ao início do seguinte com linha recta).
  const [segments, setSegments] = useState([]);
  const [bounds, setBounds] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // 🛰️ Satélite por baixo + mapa topo com transparência por cima (igual à app)
  const [satOn, setSatOn] = useState(false);
  const [legendOpen, toggleLegend, closeLegend] = useLegendToggle();
  const [topoOpacity, setTopoOpacity] = useState(0.45);
  // POIs externos (OpenStreetMap Overpass): combustível, dormidas, comida
  const [extCats, setExtCats] = useState({ fuel: false, lodging: false, food: false });

  useEffect(() => {
    const loadRoute = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const response = await axios.get(`${API}/routes/${route.route_id}/content`);
        const xmlText = response.data;
        
        // Parse XML
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xmlText, 'text/xml');
        
        let geoJSON;
        
        if (route.file_type === 'gpx') {
          geoJSON = toGeoJSON.gpx(xmlDoc);
        } else if (route.file_type === 'kml') {
          geoJSON = toGeoJSON.kml(xmlDoc);
        }
        
        // Extract coordinates — UM segmento INDEPENDENTE por LineString /
        // trkseg / parte de MultiLineString. Os Points (waypoints/POIs)
        // são ignorados — já são renderizados como Markers separados.
        const segs = [];
        const allCoords = []; // para fitBounds
        
        if (geoJSON && geoJSON.features) {
          // Heurística: o TRILHO REAL tem tipicamente centenas/milhares de
          // pontos. "Rotas através de waypoints" (GPX <rte> ou LineStrings
          // KML que conectam apenas POIs) têm poucos pontos. Para filtrar
          // estas rectas indesejadas calculamos o MAIOR LineString e
          // descartamos todos os outros com menos de 20% do tamanho dele
          // (com mínimo absoluto de 20 pontos).
          const candidateSegs = [];
          geoJSON.features.forEach((feature) => {
            // Filtro 1 — GPX <rte> (rota planeada) explicito
            const gpxType = feature.properties?._gpxType;
            if (gpxType === 'rte') return;

            if (feature.geometry.type === 'LineString') {
              const seg = feature.geometry.coordinates.map(([lon, lat]) => [lat, lon]);
              if (seg.length >= 2) candidateSegs.push(seg);
            } else if (feature.geometry.type === 'MultiLineString') {
              feature.geometry.coordinates.forEach((lineString) => {
                const seg = lineString.map(([lon, lat]) => [lat, lon]);
                if (seg.length >= 2) candidateSegs.push(seg);
              });
            }
            // Points (POIs) → ignorados aqui; renderizados como Markers
            // via route.pois mais abaixo.
          });

          // Filtro 2 — descartar segmentos suspeitamente curtos (linhas
          // rectas a conectar waypoints). Tamanho mínimo: max(20, 20% do
          // maior segmento).
          const maxLen = candidateSegs.reduce((m, s) => Math.max(m, s.length), 0);
          const minLen = Math.max(20, Math.floor(maxLen * 0.2));
          candidateSegs.forEach((seg) => {
            if (seg.length >= minLen) {
              segs.push(seg);
              allCoords.push(...seg);
            }
          });
          // Fallback — se filtrámos TUDO (todos os segmentos eram pequenos)
          // aceitamos o maior para pelo menos mostrar algo.
          if (segs.length === 0 && candidateSegs.length > 0) {
            const biggest = candidateSegs.reduce((a, b) => (a.length >= b.length ? a : b));
            segs.push(biggest);
            allCoords.push(...biggest);
          }
        }
        
        if (segs.length > 0) {
          setSegments(segs);
          setBounds(allCoords);
        } else {
          setError('Nenhuma coordenada encontrada no ficheiro');
        }
      } catch (err) {
        console.error('Error loading route:', err);
        setError('Erro ao carregar rota');
      } finally {
        setLoading(false);
      }
    };
    
    if (route) {
      loadRoute();
    }
  }, [route]);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-muted" data-testid="map-loading">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">A carregar mapa...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center bg-muted" data-testid="map-error">
        <div className="text-center">
          <p className="text-destructive mb-2">{error}</p>
        </div>
      </div>
    );
  }

  if (segments.length === 0) {
    return (
      <div className="h-full flex items-center justify-center bg-muted" data-testid="map-no-data">
        <p className="text-muted-foreground">Nenhum dado de rota disponível</p>
      </div>
    );
  }

  // Primeiro e último ponto da trilha global (junta segmentos pela ordem)
  const firstPoint = segments[0][0];
  const lastSegment = segments[segments.length - 1];
  const lastPoint = lastSegment[lastSegment.length - 1];
  const center = firstPoint || [39.5, -8.0];

  return (
    <div ref={wrapRef} className="relative isolate z-0 h-full w-full bg-background">
    <MapContainer
      center={center}
      zoom={13}
      maxZoom={19}
      scrollWheelZoom={true}
      dragging={true}
      touchZoom={true}
      doubleClickZoom={true}
      boxZoom={true}
      keyboard={true}
      zoomControl={true}
      style={{ height: '100%', width: '100%' }}
      data-testid="route-map"
    >
      {satOn ? (
        <>
          <TileLayer
            key="sat-base"
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            attribution='Tiles &copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics'
            maxZoom={19}
            maxNativeZoom={19}
          />
          {topoOpacity > 0 && (
            <TileLayer
              key="topo-overlay"
              url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
              opacity={topoOpacity}
              maxZoom={19}
              maxNativeZoom={17}
            />
          )}
        </>
      ) : (
        <AutoTopoTileLayer />
      )}

      {/* Uma Polyline INDEPENDENTE por segmento — evita linhas rectas
          entre o fim de um trkseg e o início do próximo. */}
      {segments.map((seg, idx) => (
        <Polyline
          key={`seg-${idx}`}
          positions={seg}
          pathOptions={{
            color: ROUTE_COLOR,
            weight: 5,
            opacity: 0.9,
          }}
        />
      ))}

      {/* Setas direccionais — usam todos os pontos concatenados visualmente
          mas só servem para indicar sentido (já é OK ter saltos). */}
      <DirectionArrows coordinates={segments.flat()} />

      <Marker position={firstPoint} icon={startIcon}>
        <Popup><b style={{ color: '#16A34A' }}>Início</b> da rota</Popup>
      </Marker>
      <Marker position={lastPoint} icon={endIcon}>
        <Popup><b style={{ color: '#DC2626' }}>Fim</b> da rota</Popup>
      </Marker>

      {(route.pois || []).map((poi, idx) => (
        <Marker
          key={poi.id || `poi-${idx}`}
          position={[poi.latitude, poi.longitude]}
          icon={poiIcon}
        >
          <Popup>
            <div className="text-sm">
              <div className="font-bold text-base mb-1">{poi.name}</div>
              {poi.description && <div className="text-slate-600">{poi.description}</div>}
              {poi.info && (
                <div className="text-slate-700 mt-1 whitespace-pre-wrap text-xs bg-amber-50 border border-amber-200 rounded p-1.5">
                  ℹ️ {poi.info}
                </div>
              )}
              {poi.elevation != null && (
                <div className="text-xs text-slate-500 mt-1">Elevação: {Math.round(poi.elevation)} m</div>
              )}
            </div>
          </Popup>
        </Marker>
      ))}
      
      <ExternalPoisLayer bounds={bounds} enabledCats={extCats} />

      {/* Municipality live overlay — só renderiza se explicitamente ligado.
          Backend gate impede utilizadores sem permissão de ver dados. */}
      <MunicipioLiveLayers routeId={route?.route_id} enabled={liveOverlay && liveOverlayMode === 'municipio'} />
      <CompanyLiveLayers companyId={companyId} enabled={liveOverlay && liveOverlayMode === 'company'} />

      <FitBounds bounds={bounds} routeId={route?.route_id} />
      <FlyToPOI poi={focusPOI} />
      <ResizeOnFullscreen />
    </MapContainer>

    <FullscreenButton targetRef={wrapRef} />
    {legendOpen && <WebMapLegendPanel onClose={closeLegend} />}

    {/* Painel de camadas — satélite + POIs externos */}
    <div className="absolute top-20 left-3 z-[500] flex flex-col gap-1.5" data-testid="map-layers-panel">
      <WebMapLegendButton open={legendOpen} onToggle={toggleLegend} />
      <button
        onClick={() => setSatOn((v) => !v)}
        className={`px-2.5 py-1.5 rounded-lg text-xs font-bold shadow-md border text-left transition-colors ${
          satOn ? 'bg-slate-900 text-white border-slate-900' : 'bg-white/95 text-slate-700 border-slate-200 hover:border-slate-400'
        }`}
        data-testid="toggle-satellite-btn"
        title="Satélite por baixo com mapa transparente por cima"
      >
        🛰️ Satélite
      </button>
      {satOn && (
        <div className="bg-white/95 rounded-lg shadow-md border border-slate-200 px-2 py-1.5" data-testid="sat-opacity-control">
          <div className="text-[10px] font-bold text-slate-600 mb-0.5">Mapa {Math.round(topoOpacity * 100)}%</div>
          <input
            type="range"
            min="0"
            max="90"
            value={Math.round(topoOpacity * 100)}
            onChange={(e) => setTopoOpacity(Number(e.target.value) / 100)}
            className="w-24"
            data-testid="sat-opacity-slider"
          />
        </div>
      )}
      {Object.entries(EXTERNAL_POI_CATEGORIES).map(([key, cat]) => (
        <button
          key={key}
          onClick={() => setExtCats((prev) => ({ ...prev, [key]: !prev[key] }))}
          className={`px-2.5 py-1.5 rounded-lg text-xs font-bold shadow-md border text-left transition-colors ${
            extCats[key] ? 'bg-emerald-600 text-white border-emerald-700' : 'bg-white/95 text-slate-700 border-slate-200 hover:border-emerald-400'
          }`}
          data-testid={`toggle-extpoi-${key}`}
          title={`Mostrar/esconder ${cat.label} (OpenStreetMap)`}
        >
          {cat.emoji} {cat.label}
        </button>
      ))}
    </div>
    </div>
  );
};

export default RouteMap;
